import { useState } from 'react';
import {
  extractApiErrorCode,
  extractApiErrorStatus,
  isApiError,
} from '../errors/api-error';
import type { ChangePasswordInput } from './reauthenticate-and-change-password';

/**
 * O que o hook precisa do app. Três operações, e a ordem entre elas é o que
 * este módulo existe para garantir.
 */
export interface ForcedPasswordChangePort<TSession> {
  /** Reautentica com a temporária, troca, devolve o e-mail. */
  reauthenticateAndChangePassword(input: ChangePasswordInput): Promise<string>;
  /** Re-login com a senha NOVA — a troca revogou todas as sessões. */
  signIn(input: { email: string; password: string }): Promise<TSession>;
  /**
   * Derruba a sessão. **Precisa esperar o `signOut`**: a tela de login entra
   * sozinha quando encontra sessão Firebase viva, e um `signOut` ainda em vôo
   * faria essa entrada automática sair com a sessão que acabamos de invalidar.
   */
  clearAuth(): Promise<void>;
}

/** Mensagens de erro do Firebase que valem para qualquer app. */
const FIREBASE_MESSAGES: Record<string, string> = {
  'auth/wrong-password': 'Senha atual incorreta.',
  'auth/invalid-credential': 'Senha atual incorreta.',
  'auth/too-many-requests':
    'Muitas tentativas. Aguarde alguns minutos e tente de novo.',
};

/** Códigos de domínio do backend que ambos os apps traduzem igual. */
const BACKEND_MESSAGES: Record<string, string> = {
  REAUTHENTICATION_REQUIRED: 'Confirme sua senha novamente e tente de novo.',
  // 404 no change-password: o uid autenticado não resolve para uma conta. Não é
  // erro de digitação — a sessão não serve mais.
  ACCOUNT_NOT_FOUND: 'Sessão expirada. Entre novamente.',
};

/**
 * 400 do `ValidationPipe`: o backend recusou o **valor**, e essa resposta **não
 * carrega código de domínio** — o corpo é `{ statusCode, error: 'Bad Request',
 * message: string[] }`, com o texto em inglês do class-validator.
 *
 * Chegar aqui significa que a regra do formulário divergiu da do backend, porque
 * o form já barra senha curta antes de enviar. É estado de bug nosso, não erro
 * de quem está na tela — então a mensagem não tenta adivinhar a regra: dizer
 * "mínimo 8 caracteres" quando o motivo real é outro é pior que não dizer.
 */
const VALIDATION_REJECTED =
  'O servidor não aceitou esta senha. Tente outra — se continuar, avise o suporte.';

/** Recusa com resposta HTTP que não sabemos traduzir. */
const BACKEND_REFUSED = 'Não foi possível trocar a senha. Tente de novo.';

/**
 * O 403 que o re-login **não deveria** ver: acabamos de trocar a senha, e o
 * `ChangePasswordUseCase` limpa o `mustChangePassword` antes de responder.
 *
 * Quando aparece, o backend ainda não enxerga a troca — leitura atrasada da
 * conta. E é um laço, não um erro isolado: mandar a pessoa ao login faz o
 * `useSignIn` devolvê-la a esta tela com a senha NOVA no papel de "temporária",
 * e o formulário recusa repeti-la. Observado em teste manual (2026-09-04): a
 * pessoa só escapa escolhendo uma terceira senha.
 *
 * Daí a repetição: o estado é transitório por natureza, então uma segunda
 * tentativa depois de um instante resolve o caso comum sem envolver quem está
 * na tela.
 */
const STALE_FLAG_CODE = 'PASSWORD_CHANGE_REQUIRED';
const DEFAULT_STALE_FLAG_RETRY_MS = 1500;

function firebaseErrorCode(err: unknown): string {
  return typeof err === 'object' && err !== null && 'code' in err
    ? String((err as { code: unknown }).code)
    : '';
}

/**
 * Traduz erro do Firebase (reauth) ou do backend para uma mensagem de tela.
 *
 * O fallback é `err.message`, que num erro do axios é "Request failed with
 * status code 403" — string de desenvolvedor. Por isso `backendMessages`: cada
 * app cobre os códigos que o seu backend devolve sem precisar reimplementar a
 * tradução inteira.
 */
export function messageForError(
  err: unknown,
  backendMessages: Record<string, string> = {},
): string {
  // 1. Firebase (a reautenticação).
  const firebase = FIREBASE_MESSAGES[firebaseErrorCode(err)];
  if (firebase) return firebase;

  // 2. Código de domínio do backend; o app tem precedência sobre o padrão.
  const code = extractApiErrorCode(err);
  if (code) {
    const backend = backendMessages[code] ?? BACKEND_MESSAGES[code];
    if (backend) return backend;
  }

  // 3. O backend respondeu, mas sem código que conheçamos. **Nunca** cair em
  //    `err.message` aqui: num erro do axios ele é "Request failed with status
  //    code 400" — string de desenvolvedor na cara de quem está na tela.
  if (isApiError(err)) {
    return extractApiErrorStatus(err) === 400
      ? VALIDATION_REJECTED
      : BACKEND_REFUSED;
  }

  // 4. Sem resposta HTTP: erro nosso ou do Firebase sem código conhecido. Aqui
  //    `err.message` É a mensagem certa — é a que nós escrevemos (ex.: o
  //    "Sessão expirada. Entre novamente." de `reauthenticateAndChangePassword`).
  return err instanceof Error ? err.message : BACKEND_REFUSED;
}

export interface UseForcedPasswordChangeOptions<TSession> {
  port: ForcedPasswordChangePort<TSession>;
  /**
   * A troca deu certo E o re-login também: o app grava a sessão e navega.
   * Fica com o app porque o destino difere (`/` no admin, `/profile` no user).
   */
  onAuthenticated: (session: TSession, email: string) => void;
  /**
   * A senha MUDOU mas o re-login falhou. A sessão já foi derrubada (esperada)
   * quando isto roda; o app leva a pessoa ao login com a mensagem.
   *
   * Recebe também o erro que causou a perda — é o **único** ponto do fluxo que
   * redireciona, então sem isto uma falha aqui fica indistinguível de qualquer
   * outra para quem está na tela e para quem depura.
   */
  onSessionLost: (message: string, cause: unknown) => void;
  /** Códigos do backend deste app → mensagem de tela. */
  backendMessages?: Record<string, string>;
  /**
   * Espera antes de repetir o re-login quando o backend ainda vê a troca como
   * pendente. Injetável para o teste não esperar de verdade.
   */
  staleFlagRetryDelayMs?: number;
}

const SESSION_LOST_MESSAGE =
  'Senha alterada. Entre novamente com a nova senha.';

/**
 * Troca obrigatória de senha, com o re-login silencioso que ela exige.
 *
 * A sequência tem dois desfechos e eles NÃO são simétricos:
 *
 *  - falha na troca → a senha não mudou, a sessão atual ainda vale, e a pessoa
 *    fica na tela para corrigir. Não derruba nada;
 *  - troca ok mas re-login falha → a senha JÁ mudou e todas as sessões caíram.
 *    Insistir com a antiga não adianta: derruba a sessão (esperando) e manda ao
 *    login com o recado.
 */
export function useForcedPasswordChange<TSession>({
  port,
  onAuthenticated,
  onSessionLost,
  backendMessages,
  staleFlagRetryDelayMs = DEFAULT_STALE_FLAG_RETRY_MS,
}: UseForcedPasswordChangeOptions<TSession>) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const changePassword = async (input: ChangePasswordInput) => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    let email: string;
    try {
      // Reautentica + troca. Se falhar aqui, a senha NÃO mudou.
      email = await port.reauthenticateAndChangePassword(input);
    } catch (err) {
      setError(messageForError(err, backendMessages));
      setIsLoading(false);
      return;
    }

    const credentials = { email, password: input.newPassword };
    try {
      let session: TSession;
      try {
        session = await port.signIn(credentials);
      } catch (err) {
        // Uma única repetição, e só para o flag atrasado — ver STALE_FLAG_CODE.
        // Qualquer outra falha é relançada: repetir senha errada ou conta
        // desabilitada não melhora com espera, e insistir só rende throttle.
        if (extractApiErrorCode(err) !== STALE_FLAG_CODE) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, staleFlagRetryDelayMs),
        );
        session = await port.signIn(credentials);
      }
      setSuccess(true);
      onAuthenticated(session, email);
    } catch (err) {
      // Esperado, não solto: a tela de login entra sozinha se encontrar sessão
      // Firebase viva, e a desta senha já foi revogada — a entrada automática
      // sairia com token morto e trocaria o recado por um erro de 401.
      await port.clearAuth();
      // O erro vai junto. Ele era descartado aqui, e o custo apareceu em teste
      // manual: a pessoa foi devolvida ao login sem saber por quê, e nós também
      // não sabíamos — o único caminho que redireciona é este.
      onSessionLost(SESSION_LOST_MESSAGE, err);
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setError(null);
    setSuccess(false);
  };

  return { changePassword, reset, isLoading, error, success };
}
